import React from 'react';

/**
 * Reusable Teeter-Totter / Load Balance visual.
 * Renders a see-saw style balance between BHP (internal capacity) and PEI (external pressure).
 *
 * Props:
 *   bhp      - BHP score (0–1 scale)
 *   pei      - PEI score (0–1 scale)
 *   compact  - if true, renders a smaller inline version
 *   label    - optional override label (e.g. "Financial Load Balance")
 */
const TeeterTotter = ({ bhp = 0.5, pei = 0.5, compact = false, label }) => {
  const delta = pei - bhp;
  const absDelta = Math.abs(delta);

  let tiltAngle = 0;
  let tiltLabel = 'Balanced';
  if (absDelta < 0.01) { tiltAngle = 0; tiltLabel = 'Balanced'; }
  else if (absDelta < 0.05) { tiltAngle = 4; tiltLabel = delta > 0 ? 'Balanced Under Load' : 'Capacity Supported'; }
  else if (absDelta < 0.15) { tiltAngle = 8; tiltLabel = delta > 0 ? 'Moderate Strain' : 'Moderate Surplus'; }
  else if (absDelta < 0.3) { tiltAngle = 13; tiltLabel = delta > 0 ? 'High Strain' : 'High Surplus'; }
  else { tiltAngle = 15; tiltLabel = delta > 0 ? 'Critical Overload' : 'Surplus Capacity'; }

  const tiltDirection = delta > 0 ? tiltAngle : -tiltAngle;
  const bhpPct = Math.round(bhp * 100);
  const peiPct = Math.round(pei * 100);

  // Pressure level
  let pressureLevel = 'Low';
  let pressureColor = '#22C55E';
  if (pei >= 0.7) { pressureLevel = 'Critical'; pressureColor = '#DC2626'; }
  else if (pei >= 0.6) { pressureLevel = 'High'; pressureColor = '#EA580C'; }
  else if (pei >= 0.4) { pressureLevel = 'Moderate'; pressureColor = '#EAB308'; }

  if (compact) {
    return (
      <div className="bg-gradient-to-r from-blue-50 via-gray-50 to-red-50 rounded-xl p-3 border border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-gray-500">
            {label || 'Load Balance'}
          </span>
          <span className="text-xs font-bold" style={{ color: pressureColor }}>
            {tiltLabel}
          </span>
        </div>
        <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="absolute left-0 top-0 h-full rounded-l-full transition-all duration-700"
            style={{ width: `${Math.min(100, (bhp / (bhp + pei || 1)) * 100)}%`, backgroundColor: '#3B82F6' }}
          />
          <div
            className="absolute right-0 top-0 h-full rounded-r-full transition-all duration-700"
            style={{ width: `${Math.min(100, (pei / (bhp + pei || 1)) * 100)}%`, backgroundColor: '#EF4444' }}
          />
          <div className="absolute left-1/2 top-0 h-full w-0.5 bg-gray-600 transform -translate-x-1/2" />
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-xs text-blue-600 font-semibold">BHP {bhpPct}%</span>
          <span className="text-xs text-red-500 font-semibold">PEI {peiPct}%</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 via-white to-red-50 rounded-2xl shadow-md p-5 border border-gray-100">
      <h3 className="text-base font-bold text-gray-800 mb-1 flex items-center gap-2">
        <span>⚖️</span> {label || 'Teeter-Totter / Load Balance'}
      </h3>
      <p className="text-xs text-gray-400 mb-4">
        How your internal capacity (BHP) interacts with environmental pressure (PEI)
      </p>

      {/* SVG see-saw */}
      <div className="flex justify-center mb-4">
        <svg viewBox="0 0 320 120" width="100%" style={{ maxWidth: 400 }}>
          {/* Fulcrum */}
          <polygon points="160,110 145,85 175,85" fill="#9CA3AF" />

          {/* Beam */}
          <g transform={`rotate(${tiltDirection}, 160, 85)`}>
            <rect x="30" y="80" width="260" height="6" rx="3" fill="#D1D5DB" />

            {/* BHP side */}
            <rect x="40" y="58" width="50" height="22" rx="6" fill="#3B82F6" />
            <text x="65" y="73" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">
              BHP
            </text>
            <text x="65" y="53" textAnchor="middle" fill="#3B82F6" fontSize="11" fontWeight="bold">
              {bhpPct}%
            </text>

            {/* PEI side */}
            <rect x="230" y="58" width="50" height="22" rx="6" fill="#EF4444" />
            <text x="255" y="73" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">
              PEI
            </text>
            <text x="255" y="53" textAnchor="middle" fill="#EF4444" fontSize="11" fontWeight="bold">
              {peiPct}%
            </text>
          </g>
        </svg>
      </div>

      {/* Status chips */}
      <div className="flex flex-wrap justify-center gap-2">
        <span className="inline-flex items-center gap-1 text-xs rounded-full border px-3 py-1"
          style={{ background: '#F8FAFC', borderColor: '#E5E7EB' }}>
          <span className="font-semibold" style={{ color: '#6B7280' }}>System:</span>
          <span className="font-bold" style={{ color: '#111827' }}>{tiltLabel}</span>
        </span>
        <span className="inline-flex items-center gap-1 text-xs rounded-full border px-3 py-1"
          style={{ background: '#F8FAFC', borderColor: '#E5E7EB' }}>
          <span className="font-semibold" style={{ color: '#6B7280' }}>Pressure:</span>
          <span className="font-bold" style={{ color: pressureColor }}>{pressureLevel}</span>
        </span>
      </div>
    </div>
  );
};

export default TeeterTotter;
